import { useEffect, useState } from "react";
import { Link as NavLink, useNavigate, useSearchParams } from "react-router";

import * as Api from "@/api";
import InputPassword from "@/design/forms/InputPassword";
import { string } from "@/utils/hooks/endorse";
import useSave, { SaveMode } from "@/utils/hooks/useSave";
import Sesame from "@/utils/Sesame";
import Valkyrie, { viArrowRight, viShieldKeyhole, viSpinnerThird } from "@sippy-platform/valkyrie";

import { Button, Link, Stack, Typography } from "@mui/material";

import authReducer from "./authReducer";
import AuthUI from "./AuthUI";

export default function ResetPassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [success, setSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    if (!searchParams.get("token")) {
      navigate("/login");
    }
  }, [navigate, searchParams]);

  const { isValid, value, dispatch, errors, save, pending } = useSave({
    initialValue: { password: "", confirmPassword: "" },
    mode: SaveMode.MANUAL,
    reducer: authReducer,
    validator: () => ({
      password: string()
        .required({ message: "Password is required." })
        .length([8], { message: "Password must be at least 8 characters." })
        .pattern("^(?=.*?[a-zA-Z])(?=.*?[0-9]).*$", {
          message: "Password should have at least one letter and one number.",
        })
        .truthy(value.confirmPassword === value.password, { message: "Passwords do not match." }),
    }),
    onSubmit: () => Api.postResetPassword(value, searchParams.get("token")),
    onSuccess: () => setSuccess(true),
  });

  // Handle the password
  function generatePassword() {
    const newPassword = new Sesame(16).generate();
    dispatch({ type: "resetPassword", value: newPassword });
    setShowPassword(true);
  }

  return (
    <AuthUI
      onSubmit={(e) => {
        e.preventDefault();
        save();
      }}
    >
      <title>{`Reset password \u00b7 ${import.meta.env.SIPPY_APP_TITLE}`}</title>
      <Typography variant="h4" sx={{ m: 0 }}>
        Reset password
      </Typography>
      {success ? (
        <>
          <Typography sx={{ mb: 2 }}>Your password has been successfully updated.</Typography>
          <Button
            sx={{ width: "100%", fs: "1rem", boxShadow: 0 }}
            variant="contained"
            size="large"
            component={NavLink}
            to="/login"
            endIcon={<Valkyrie icon={viArrowRight} />}
          >
            Go to login
          </Button>
        </>
      ) : (
        <>
          <Typography sx={{ mb: 2 }}>Set a new password for your account.</Typography>
          <InputPassword
            required
            showPasswordStrength
            showPassword={showPassword}
            placeholder="Password"
            id="password"
            label="Password"
            value={value.password}
            onChange={(id, value) => dispatch({ type: id, value })}
            error={errors}
          />
          <InputPassword
            required
            placeholder="Confirm password"
            id="confirmPassword"
            label="Confirm password"
            value={value.confirmPassword}
            onChange={(id, value) => dispatch({ type: id, value })}
            error={errors}
          />
          <Stack
            direction="row"
            sx={{
              justifyContent: "space-between",
            }}
          >
            <Link
              onClick={generatePassword}
              component="button"
              type="button"
              color="inherit"
              underline="hover"
              variant="body2"
            >
              Generate password
            </Link>
            <Link component={NavLink} to="/login" color="inherit" underline="hover" variant="body2">
              Login
            </Link>
          </Stack>
          <Button
            variant="contained"
            size="large"
            id="submit"
            type="submit"
            autoFocus
            endIcon={<Valkyrie icon={pending ? viSpinnerThird : viShieldKeyhole} spin={pending} />}
            disabled={pending || !isValid}
          >
            Reset password
          </Button>
        </>
      )}
    </AuthUI>
  );
}
